import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Avatar,
  Button,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import {
  LiveTv as LiveTvIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  PlayArrow as PlayIcon,
  ConfirmationNumber as TicketIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import CustomTab from "../../../common/CustomTab";
import {
  useGetOnAirCard,
  CardWithTalent,
  useCloverCardPurchaseList,
} from "../../../../services/api/card.api";

import { useAuth } from "../../../../hooks/useAuth";
import { Icons } from "../../../assets/icon";
import { Talent } from "@/types/user";

// Types for ticket data
interface TicketData {
  id: string;
  title: string;
  talent: Talent;
  talentId: string;
  avatar: string;
  date: string;
  timeRange: string;
  ticketPrice: number;
  isLive: boolean;
  hasAccess: boolean;
  shortDescription: string;
  primaryThumbnail?: string;
  startDateTime: string;
  endDateTime: string;
}

// Tab configuration
const tabs = [
  { label: "All Tickets", value: "all" },
  { label: "Live Now", value: "live" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Past Events", value: "past" },
];

const TicketSession: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [filter, setFilter] = useState<"all" | "live" | "upcoming" | "past">(
    "all"
  );

  // Fetch on-air events (this would typically be replaced with user's purchased tickets)
  const {
    data: cloverCardPurchaseList,
    isLoading,
    error,
  } = useCloverCardPurchaseList({
    key: ["clover-card-purchase-list"],
    params: {
      offset: 0,
      limit: 1000,
    },
    options: {
      refetchInterval: 10000,
    },
  });

  const purchasedTickets: TicketData[] = useMemo(() => {
    if (!cloverCardPurchaseList?.data) return [];

    return cloverCardPurchaseList.data
      .filter((event: CardWithTalent) => event.suit === "Clover")
      .map((event: CardWithTalent) => {
        const startTime = dayjs(event.start_date_time);
        const endTime = startTime.add(event.duration, "minute");

        // Check if event is live based on time OR status is "Ongoing"
        const isLiveByStatus = (event as any).status === "Ongoing";
        const isLive =  isLiveByStatus;

        return {
          id: event.id.toString(),
          title: event.title,
          talent: event.talent,
          talentId: event.talent.id,
          avatar:
            (typeof event.primary_thumbnail === "string"
              ? event.primary_thumbnail
              : "") ||
            event.talent.profile_image ||
            event.talent.username[0],

          date: startTime.format("DD MMM YYYY"),
          timeRange: `${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}`,
          ticketPrice: event.price,
          isLive,
          hasAccess: true, // User has purchased this ticket
          shortDescription: event.short_description || "Live performance event",
          primaryThumbnail:
            typeof event.primary_thumbnail === "string"
              ? event.primary_thumbnail
              : undefined,
          startDateTime: event.start_date_time || "",
          endDateTime: event.start_date_time
            ? dayjs(event.start_date_time)
                .add(event.duration, "minute")
                .toISOString()
            : "",
        };
      });
  }, [cloverCardPurchaseList]);

  // Filter tickets based on selected tab
  const filteredTickets = useMemo(() => {
    if (filter === "all") return purchasedTickets;

    return purchasedTickets.filter((ticket) => {
      const startTime = dayjs(ticket.startDateTime);
      const endTime = dayjs(ticket.endDateTime);
      const now = dayjs();

      switch (filter) {
        case "live":
          return (
            ticket.isLive
          );
        case "upcoming":
          return startTime.isAfter(now);
        case "past":
          return endTime.isBefore(now);
        default:
          return true;
      }
    });
  }, [purchasedTickets, filter]);

  const handleJoinLiveSession = (ticket: TicketData) => {
    if (!ticket.isLive) {
      enqueueSnackbar("This event is not live yet", { variant: "warning" });
      return;
    }

    navigate(`/user-live-stream/${ticket.id}`);
  };

  const handleViewEventDetails = (ticket: TicketData) => {
    navigate(`/cards/${ticket.id}`);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 1, sm: 2 },
          py: { xs: 2, sm: 4 },
        }}
      >
        <Stack spacing={2}>
          {[...Array(3)].map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={120} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 1, sm: 2 },
          py: { xs: 2, sm: 4 },
        }}
      >
        <Typography color="error" textAlign="center">
          Failed to load your tickets. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        px: { xs: 1, sm: 2 },
        py: { xs: 2, sm: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          mb={2}
        >
          <TicketIcon
            sx={{ fontSize: { xs: 24, sm: 32 }, color: "var(--primary-700)" }}
          />
          <Typography
            variant="h4"
            fontWeight={700}
            fontFamily="Lexend"
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            My Tickets
          </Typography>
        </Stack>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            mx: "auto",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            px: { xs: 1, sm: 0 },
          }}
        >
          Access your purchased clover card tickets and join live sessions
        </Typography>
      </Box>

      {/* Filter Tabs */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <CustomTab
          tabs={tabs}
          value={filter}
          onChange={(_: any, newValue: any) => {
            if (["all", "live", "upcoming", "past"].includes(newValue)) {
              setFilter(newValue as typeof filter);
            }
          }}
        />
      </Box>

      {/* Empty State */}
      {filteredTickets.length === 0 && (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            borderRadius: 3,
            backgroundColor: "background.paper",
          }}
        >
          <TicketIcon
            sx={{
              fontSize: { xs: 48, sm: 64 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            fontWeight={600}
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          >
            {filter === "all" && "No Tickets Found"}
            {filter === "live" && "No Live Events"}
            {filter === "upcoming" && "No Upcoming Events"}
            {filter === "past" && "No Past Events"}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 400,
              mx: "auto",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {filter === "all" &&
              "You haven't purchased any clover card tickets yet. Explore live events to get started!"}
            {filter === "live" &&
              "No events are currently live. Check back later for exciting performances!"}
            {filter === "upcoming" &&
              "No upcoming events scheduled. Stay tuned for future announcements!"}
            {filter === "past" && "No past events to display."}
          </Typography>
        </Paper>
      )}

      {/* Tickets List */}
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        {filteredTickets.map((ticket) => (
          <Paper
            key={ticket.id}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: ticket.isLive ? "2px solid" : "1px solid",
              borderColor: ticket.isLive
                ? "var(--destructive-600)"
                : "var(--neutral-100)",
              background: ticket.isLive
                ? "linear-gradient(135deg, var(--neutralWhite) 0%, var(--destructive-100) 100%)"
                : "var(--neutralWhite)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "var(--shadow-md)",
                borderColor: ticket.isLive
                  ? "var(--destructive-700)"
                  : "var(--primary-700)",
              },
              position: "relative",
              overflow: "hidden",
              "&::before": ticket.isLive
                ? {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    background:
                      "linear-gradient(180deg, var(--destructive-600) 0%, var(--destructive-700) 100%)",
                  }
                : {},
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  mr: { xs: 0, sm: 3 },
                  mb: { xs: 2, sm: 0 },
                  flexShrink: 0,
                  alignSelf: { xs: "center", sm: "flex-start" },
                }}
              >
                {ticket.primaryThumbnail ? (
                  <Box
                    sx={{
                      width: { xs: 60, sm: 80 },
                      height: { xs: 60, sm: 80 },
                      borderRadius: 2,
                      backgroundImage: `url(${ticket.primaryThumbnail})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: { xs: 60, sm: 80 },
                      height: { xs: 60, sm: 80 },
                      borderRadius: 2,
                      background: "var(--button-gradient)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "var(--neutralWhite)",
                        fontWeight: 700,
                        opacity: 0.8,
                        fontSize: { xs: "1.5rem", sm: "2.125rem" },
                      }}
                    >
                      {ticket.title.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 2, sm: 0 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    flexDirection: { xs: "column", sm: "row" },
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: "var(--neutral-900)",
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 },
                      cursor: "pointer",
                      textAlign: { xs: "center", sm: "left" },
                    }}
                    noWrap
                    onClick={() => {
                      navigate(`/cards/${ticket.id}`);
                    }}
                  >
                    {ticket.title}
                  </Typography>
                  {ticket.isLive && (
                    <Chip
                      icon={
                        <LiveTvIcon
                          sx={{ fontSize: 14, color: "var(--neutralWhite)" }}
                        />
                      }
                      label="LIVE"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        backgroundColor: "var(--destructive-600)",
                        color: "var(--neutralWhite)",
                        "& .MuiChip-icon": {
                          color: "var(--neutralWhite)",
                        },
                      }}
                    />
                  )}
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "center", sm: "center" }}
                  spacing={{ xs: 1, sm: 2 }}
                  mb={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={ticket.talent.profile_image}
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "var(--primary-700)",
                      }}
                      onClick={() => {
                        navigate(`/talent/${ticket.talent.id}`);
                      }}
                    >
                      {ticket.talent.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      variant="body2"
                      color="var(--neutral-900)"
                      fontWeight={500}
                      onClick={() => {
                        navigate(`/talent/${ticket.talent.id}`);
                      }}
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {ticket.talent.username}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--neutral-400)",
                      display: "flex",
                      alignItems: "center",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {ticket.date}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--neutral-400)",
                      display: "flex",
                      alignItems: "center",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {ticket.timeRange}
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Icons.Coin />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: "var(--primary-700)",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {ticket.ticketPrice}
                    </Typography>
                  </Stack>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    color: "var(--neutral-400)",
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitLineClamp: { xs: 2, sm: 1 },
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  {ticket.shortDescription}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  ml: { xs: 0, sm: 3 },
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                >
                  {ticket.isLive ? (
                    <Button
                      variant="contained"
                      startIcon={<PlayIcon />}
                      onClick={() => handleJoinLiveSession(ticket)}
                      fullWidth={isMobile}
                      sx={{
                        background: "var(--button-gradient)",
                        color: "var(--neutralWhite)",
                        fontWeight: 600,
                        textTransform: "none",
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1 },
                        borderRadius: 2,
                        boxShadow: "var(--shadow-sm)",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        minWidth: { xs: "auto", sm: "120px" },
                        "&:hover": {
                          background: "var(--buton-gradient-hover)",
                          boxShadow: "var(--shadow-md)",
                        },
                      }}
                    >
                      Join Live
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => handleViewEventDetails(ticket)}
                      fullWidth={isMobile}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1 },
                        borderRadius: 2,
                        borderWidth: 2,
                        borderColor: "var(--primary-700)",
                        color: "var(--primary-700)",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        minWidth: { xs: "auto", sm: "120px" },
                        "&:hover": {
                          borderWidth: 2,
                          backgroundColor: "var(--primaryAlpha)",
                        },
                      }}
                    >
                      View Details
                    </Button>
                  )}
                </Stack>
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default TicketSession;
